import tinycolor from 'tinycolor2';

export type Tema = {
	cor: string;
	dominante: string;
	escuro: boolean;
	texto: string;
	textoSuave: string;
	secundaria: string;
	botaoFundo: string;
	botaoTexto: string;
	hover: string;
};

const CORES = /rgba?\([^)]+\)|#[0-9a-f]{3,8}\b|\b(?:black|white)\b/gi;

const entre = (valor: number, min: number, max: number) => Math.min(Math.max(valor, min), max);

export const corDominante = (cor: string): string => {
	const achadas = (cor.match(CORES) ?? [])
		.map((item) => tinycolor(item))
		.filter((item) => item.isValid())
		.map((item) => item.toRgb());

	if (!achadas.length) return tinycolor(cor).toHexString();

	const media = achadas.reduce(
		(soma, { r, g, b }) => ({ r: soma.r + r, g: soma.g + g, b: soma.b + b }),
		{ r: 0, g: 0, b: 0 }
	);

	return tinycolor({
		r: Math.round(media.r / achadas.length),
		g: Math.round(media.g / achadas.length),
		b: Math.round(media.b / achadas.length)
	}).toHexString();
};

const legivel = (hex: string) => (tinycolor(hex).getBrightness() > 150 ? '#1f2937' : '#f9fafb');

const tingido = (hex: string, escuro: boolean) => {
	const { h, s } = tinycolor(hex).toHsl();
	if (s < 0.12) return escuro ? '#f9fafb' : '#1f2937';

	return tinycolor({ h, s: entre(s, 0.35, 0.7), l: escuro ? 0.82 : 0.24 }).toHexString();
};

const complementar = (hex: string) => {
	const { h, s, l } = tinycolor(hex).toHsl();
	return tinycolor({ h: (h + 10) % 360, s: 1 - 0.5 * s, l: 0.9 - 0.7 * l }).toHexString();
};

const realce = (hex: string, escuro: boolean) => {
	const { r, g, b } = tinycolor(hex).toRgb();
	const alvo = escuro ? 255 : 0;
	const puxar = (canal: number) => Math.round(canal + (alvo - canal) * 0.18);

	return tinycolor({ r: puxar(r), g: puxar(g), b: puxar(b) }).setAlpha(0.6).toRgbString();
};

export const gerarTema = (cor: string, destaque?: string): Tema => {
	const dominante = corDominante(cor);
	const escuro = tinycolor(dominante).getBrightness() <= 150;
	const acento = destaque ? corDominante(destaque) : complementar(dominante);
	const base = destaque ? acento : dominante;

	return {
		cor,
		dominante,
		escuro,
		texto: legivel(dominante),
		textoSuave: tingido(base, escuro),
		secundaria: acento,
		botaoFundo: acento,
		botaoTexto: legivel(acento),
		hover: realce(base, escuro)
	};
};
